import { range } from 'lodash-es';
import { formatDate, formatNumber } from '@angular/common';

/**
 * Vanwege corona neemt de inspectie schooljaar 2020/2021 niet mee in de beoordeling.
 * Om toch tot een beoordeling van 3 jaren te komen, nemen ze dan een extra jaar mee aan de voorkant.
 */
export const DRIE_SCHOOLJAREN_EXCL_2020 = 3.1;

/**
 * Vanwege corona neemt de inspectie schooljaar 2020/2021 niet mee in de beoordeling.
 * Om toch tot een beoordeling van 3 jaren te komen, nemen ze dan een extra jaar mee aan de voorkant.
 * Echter voor Onderwijspositie zien wij het inspectiejaar 2020/2021 als 2019/2020.
 */
export const DRIE_SCHOOLJAREN_EXCL_2019 = 3.2;

const DEFAULT_DATUM_FORMAT = 'YYYY-MM-dd';

export const MAX_DATE = new Date('3000-12-31');

export const MIN_DATE = new Date('1900-01-01');

export function addMonths(date: Date, aantal: number): Date {
	const result = new Date(date);
	result.setMonth(date.getMonth() + aantal);
	return result;
}

export function addDays(date: Date, aantal: number): Date {
	const result = new Date(date);
	result.setDate(date.getDate() + aantal);
	return result;
}

export function addYears(date: Date, aantal: number): Date {
	const result = new Date(date);
	result.setFullYear(date.getFullYear() + aantal);
	return result;
}

export function getISOWeekAndYear(d: Date): { week: number; year: number } {
	const date = new Date(d);
	date.setHours(0, 0, 0, 0);
	// Thursday in current week decides the year.
	date.setDate(date.getDate() + 3 - ((date.getDay() + 6) % 7));
	// January 4 is always in week 1.
	const week1 = new Date(date.getFullYear(), 0, 4);
	return {
		// Adjust to Thursday in week 1 and count number of weeks from date to week1.
		week: 1 + Math.round(((date.getTime() - week1.getTime()) / 86400000 - 3 + ((week1.getDay() + 6) % 7)) / 7),
		year: date.getFullYear(),
	};
}

export function getSchooljaarTovHuidig(aantalJaren: number = 0): string {
	return getSchooljaarForDate(today(), aantalJaren);
}

export function getSchooljaarForDate(huidigeDatum: Date, aantalJaren: number = 0): string {
	const datum = addMonths(huidigeDatum, -7);
	const beginjaar = datum.getFullYear() + aantalJaren;
	const eindjaar = beginjaar + 1;
	return `${beginjaar}/${eindjaar}`;
}

export function isHuidigeMaand(schooljaar: string | undefined, nr_maand: string): boolean {
	const huidigSchooljaar = getSchooljaarTovHuidig(0);
	return schooljaar === huidigSchooljaar && nr_maand === getHuidigeMaand().toString();
}

export function getSchooljaarKort(schooljaar: string): string {
	return schooljaar
		.split('/')
		.map((s) => s.substring(2))
		.join('/');
}

export function getSchooljaar(schooljaar: string, delta: number): string {
	const beginjaar = getBeginjaar(schooljaar) + delta;
	return getSchooljaarString(beginjaar);
}

export function getSchooljaarString(beginjaar: number) {
	return `${beginjaar}/${beginjaar + 1}`;
}

export function getBeginjaar(schooljaar: string): number {
	return Number.parseInt(schooljaar.substring(0, 4));
}

export function getEindjaar(schooljaar: string): number {
	return Number.parseInt(schooljaar.substring(5));
}

export function getPeiljaar(schooljaar: string): number {
	return getEindjaar(schooljaar) + 1;
}

export function getSchooljarenRange(eindjaar: string, aantalJaren: number): string[] {
	return range(aantalJaren)
		.map((j) => getSchooljaar(eindjaar, -j))
		.reverse(); // oplopend gesorteerd
}

export function getSchooljarenBetween(startSchooljaar: string, eindSchooljaar: string): string[] {
	const startBeginJaar = getBeginjaar(startSchooljaar);
	const eindBeginJaar = getBeginjaar(eindSchooljaar);
	return range(startBeginJaar, eindBeginJaar + 1).map(getSchooljaarString);
}

/**
 * Geeft een array van `aantalJaren` schooljaren, oplopend gesorteerd t/m `eindjaar`.
 * Als `aantalJaren` gelijk is aan `DRIE_SCHOOLJAREN_EXCL_2020`, wordt 2020/2021 eruit gefilterd en krijg je indien nodig een extra jaar aan het begin.
 */
export function getOnderwijsresultatenSchooljarenRange(
	eindjaar: string,
	aantalJaren: number,
	indicator: string,
	inspectieOnderwijspositie = false
): string[] {
	const peiljaar = getPeiljaar(eindjaar);
	if (['cv', 'exc'].includes(indicator) && aantalJaren === DRIE_SCHOOLJAREN_EXCL_2020) {
		switch (peiljaar) {
			case 2022:
				return ['2018/2019', '2019/2020'];
			case 2023:
				return ['2018/2019', '2019/2020'];
			case 2024:
				return ['2019/2020', '2022/2023'];
			case 2025:
				return ['2022/2023', '2023/2024'];
		}
	} else if (
		peiljaar === 2023 &&
		(aantalJaren === DRIE_SCHOOLJAREN_EXCL_2019 || (aantalJaren === DRIE_SCHOOLJAREN_EXCL_2020 && ['op'].includes(indicator)))
	) {
		return inspectieOnderwijspositie ? ['2019/2020', '2021/2022', '2022/2023'] : ['2018/2019', '2020/2021', '2021/2022'];
	} else if (['bbs', 'obs'].includes(indicator) && aantalJaren === DRIE_SCHOOLJAREN_EXCL_2020) {
		switch (peiljaar) {
			case 2023:
				return ['2018/2019', '2019/2020', '2021/2022'];
			case 2024:
				return ['2019/2020', '2021/2022', '2022/2023'];
		}
	}

	// voor benaming van de schooljaren door de Inspectie voor Onderwijspositie: tel er 1 bij op;
	return getSchooljarenRange(eindjaar, Math.floor(aantalJaren)).map((schooljaar) =>
		inspectieOnderwijspositie ? getSchooljaar(schooljaar, 1) : schooljaar
	);
}

/**
 * @returns true als in het opgegeven eindschooljaar rekening moet worden gehouden met het coronajaar.
 *      Voor Onderwijspositie is het coronajaar 2019/2020 en zou daarom al in 2022/2023 (=peiljaar 2024) vervallen.
 *      Voor Examencijfers zijn de coronajaren 2020/2021 en 2021/2022, en zou daarom pas in 2025/2026 (=peiljaar 2025) vervallen.
 *      Voor de overige twee indicatoren is het coronajaar 2020/2021.
 * 		Voor de user experience is het beter als over alle dashboards heen wel of niet rekening wordt gehouden met de coronajaren.
 */
export function isPeiljaarMetExclusief(eindschooljaar: string | undefined): boolean {
	return eindschooljaar !== undefined && [2022, 2023, 2024, 2025].includes(getPeiljaar(eindschooljaar));
}

export function getOnderwijsresultatenSchooljaarRangeString(
	eindjaar: string,
	aantalJaren: number,
	indicator: string,
	inspectieOnderwijspositie = false
): string {
	return getOnderwijsresultatenSchooljarenRange(eindjaar, aantalJaren, indicator, inspectieOnderwijspositie).join(', ');
}

export function getSchooljaarBegin(schooljaar: string): Date {
	return stripTime(`${getBeginjaar(schooljaar)}-08-01`);
}

export function getSchooljaarEind(schooljaar: string): Date {
	return stripTime(`${getEindjaar(schooljaar)}-07-31`);
}

export function getKalenderjaar(schooljaar: string, maand: number): number {
	return maand >= 8 ? getBeginjaar(schooljaar) : getEindjaar(schooljaar);
}

/**
 * Geeft de eerste dag van een maand binnen een schooljaar (aug t/m dec → beginjaar, jan t/m jul → eindjaar).
 */
export function getEersteDagVanMaandInSchooljaar(schooljaar: string, maand: number): Date {
	const jaar = getKalenderjaar(schooljaar, maand);
	return stripTime(new Date(jaar, maand - 1, 1));
}

/**
 * Geeft de laatste dag van een maand binnen een schooljaar (aug t/m dec → beginjaar, jan t/m jul → eindjaar).
 */
export function getLaatsteDagVanMaandInSchooljaar(schooljaar: string, maand: number): Date {
	const jaar = maand >= 8 ? getBeginjaar(schooljaar) : getEindjaar(schooljaar);
	return stripTime(new Date(jaar, maand, 0));
}

const maandLabelKort = ['jan', 'feb', 'mrt', 'apr', 'mei', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];

export function getMaandLabelKort(nr_maand: string | number): string {
	return maandLabelKort[Number(nr_maand) - 1];
}

export function getMaandLabelKortVoorlopig(schooljaar: string | undefined, nr_maand: string | number): string {
	const maand = getMaandLabelKort(nr_maand);
	const voorlopig = isHuidigeMaand(schooljaar, nr_maand.toString()) ? ' (voorl)' : '';
	return `${maand}${voorlopig}`;
}

export function getMaandNrFromLabelKort(label: string): number {
	return maandLabelKort.indexOf(label.split(' ')[0]) + 1;
}

const maandLabelLang = ['Januari', 'Februari', 'Maart', 'April', 'Mei', 'Juni', 'Juli', 'Augustus', 'September', 'Oktober', 'November', 'December'];

export function getMaandLabelLang(nr_maand: string | number): string {
	return maandLabelLang[Number(nr_maand) - 1];
}

export function getMaandLabelLangVoorlopig(schooljaar: string | undefined, nr_maand: string | number, metJaar = false): string {
	const maand = [getMaandLabelLang(nr_maand)];
	const jaar = metJaar && schooljaar ? [getKalenderjaar(schooljaar, Number(nr_maand))] : [];
	const voorlopig = isHuidigeMaand(schooljaar, nr_maand.toString()) ? ['(voorlopig)'] : [];

	return [...maand, ...jaar, ...voorlopig].join(' ');
}

const weekdagLabelKort = ['Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za', 'Zo'];

export function getWeekdagLabelKort(nr_weekdag: string | number): string {
	return weekdagLabelKort[Number(nr_weekdag) - 1];
}

export function getWeekdagNrFromLabelKort(label: string): number {
	return weekdagLabelKort.indexOf(label) + 1;
}

const weekdagLabelLang = ['Maandag', 'Dinsdag', 'Woensdag', 'Donderdag', 'Vrijdag', 'Zaterdag', 'Zondag'];

export function getWeekdagLabelLang(nr_weekdag: string | number): string {
	return weekdagLabelLang[Number(nr_weekdag) - 1];
}

export function getDatum(format: string = DEFAULT_DATUM_FORMAT, daysFromToday: number = 0): string {
	const vandaag = today();
	const morgen = addDays(vandaag, daysFromToday);
	return formatDate(morgen, format, 'nl-NL');
}

export function stripTime(date: Date | string) {
	const result = new Date(date);
	result.setHours(0, 0, 0, 0);
	return result;
}

export function today(): Date {
	return stripTime(new Date());
}

export function getNow(): string {
	return new Date().toISOString();
}

/**
 * Een Date als LocalDateTime-string zoals de backend die verwacht: ISO 8601 in lokale tijd, zonder zone.
 */
export function toLocalDateTime(datum: Date): string {
	return formatDate(datum, "yyyy-MM-dd'T'HH:mm:ss", 'nl-NL');
}

export function getTimestamp(): string {
	return formatDate(new Date(), 'yyyyMMdd-HHmmss', 'nl-NL');
}

export function dutchDate(date: Date | number | string | undefined | null): string {
	if (!date || (typeof date === 'object' && !(date instanceof Date)) || !['object', 'number', 'string'].includes(typeof date)) return '';
	return formatDate(date, 'dd-MM-yyyy', 'nl-NL');
}

export function isoDate(date: Date | number | string | undefined | null): string {
	if (!date || (typeof date === 'object' && !(date instanceof Date)) || !['object', 'number', 'string'].includes(typeof date)) return '';
	return formatDate(date, 'yyyy-MM-dd', 'nl-NL');
}

/**
 * Leest een datum in de vorm yyyy-MM-dd of dd-MM-yyyy. Geeft undefined bij een andere vorm of een datum
 * die niet bestaat (31-02-2026).
 */
export function parseDate(s: string | null | undefined): Date | undefined {
	if (typeof s !== 'string') return undefined;

	const match = /^(\d{4})-(\d{1,2})-(\d{1,2})$|^(\d{1,2})-(\d{1,2})-(\d{4})$/.exec(s.trim());
	if (!match) return undefined;

	const [jaar, maand, dag] = (match[1] ? [match[1], match[2], match[3]] : [match[6], match[5], match[4]]).map(Number);
	const datum = new Date(jaar, maand - 1, dag);
	return datum.getFullYear() === jaar && datum.getMonth() === maand - 1 && datum.getDate() === dag ? datum : undefined;
}

export function dateDiffInDays(a: Date, b: Date): number {
	const _MS_PER_DAY = 1000 * 60 * 60 * 24;
	// Discard the time and time-zone information.
	const utc1 = Date.UTC(a.getFullYear(), a.getMonth(), a.getDate());
	const utc2 = Date.UTC(b.getFullYear(), b.getMonth(), b.getDate());
	return Math.floor((utc2 - utc1) / _MS_PER_DAY);
}

export function dateDiffInHours(a: Date, b: Date): number {
	const _MS_PER_HOUR = 1000 * 60 * 60;
	const utc1 = Date.UTC(a.getFullYear(), a.getMonth(), a.getDate(), a.getHours());
	const utc2 = Date.UTC(b.getFullYear(), b.getMonth(), b.getDate(), b.getHours());
	return Math.floor((utc2 - utc1) / _MS_PER_HOUR);
}

export function equalDates(a: Date | string, b: Date | string): boolean {
	return new Date(a).getTime() === new Date(b).getTime();
}

export function compareDates(a: Date | string, b: Date | string): number {
	const dateA = new Date(a).getTime();
	const dateB = new Date(b).getTime();

	if (dateA < dateB) return -1;
	if (dateA > dateB) return 1;
	return 0;
}

export function minDate(a: Date, b: Date): Date {
	return a < b ? a : b;
}

export function maxDate(a: Date, b: Date): Date {
	return a > b ? a : b;
}

export function sameDay(a: Date, b: Date): boolean {
	return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

/**
 * Gets the start of the ISO week (Monday) for a given date
 */
export function getStartOfISOWeek(date: Date): Date {
	const result = new Date(date);
	const day = result.getDay();
	const diff = result.getDate() - day + (day === 0 ? -6 : 1); // Adjust when day is Sunday
	result.setDate(diff);
	return stripTime(result);
}

/**
 * Gets the end of the ISO week (Sunday) for a given date
 */
export function getEndOfISOWeek(date: Date): Date {
	const startOfWeek = getStartOfISOWeek(date);
	const endOfWeek = addDays(startOfWeek, 6);
	endOfWeek.setHours(23, 59, 59, 999);
	return endOfWeek;
}

/**
 * Checks if two dates are in the same ISO week
 */
export function sameISOWeek(a: Date, b: Date): boolean {
	const startOfWeekA = getStartOfISOWeek(a);
	const startOfWeekB = getStartOfISOWeek(b);
	return sameDay(startOfWeekA, startOfWeekB);
}

export type MinutesFormat = 'uur' | 'u m' | 'h' | 'h:mm' | 'hu mm';

export function formatDurationUrenMinuten(totaalMinuten: number, format: MinutesFormat) {
	const flooredUren = formatNumber(Math.floor(totaalMinuten / 60), 'nl-NL');
	const roundedUren = formatNumber(Math.round(totaalMinuten / 60), 'nl-NL');
	const restMinuten = totaalMinuten % 60;
	const restMinuten00 = String(restMinuten + 100).substring(1);

	const maybeU = Number(flooredUren) > 0 ? `${flooredUren}u ` : ``;

	switch (format) {
		case 'uur':
			return `${roundedUren} uur`;
		case 'u m':
			return `${maybeU}${restMinuten}m`;
		case 'h':
			return roundedUren;
		case 'h:mm':
			return `${flooredUren}:${restMinuten00}`;
		case 'hu mm':
			return `${flooredUren}u ${restMinuten00}m`;
	}
}

/**
 * Geeft een string die de leeftijdsgroep van de datum aangeeft, zoals "Vandaag", "Gisteren", "Deze week", "Vorige week", "2 weken geleden", "3 maanden geleden", "1 jaar geleden", etc.
 * Met "Deze week" en "Vorige week" wordt iso-week bedoeld, dus de periode van maandag tot zondag.
 */
export function getAgeGroup(dateToCompare: Date | string): string {
	const now = new Date();
	const chatDate = new Date(dateToCompare);

	// Today
	if (sameDay(now, chatDate)) {
		return 'Vandaag';
	}

	// Yesterday
	const yesterday = addDays(now, -1);
	if (sameDay(yesterday, chatDate)) {
		return 'Gisteren';
	}

	// This week (current ISO week, excluding today and yesterday)
	if (sameISOWeek(now, chatDate)) {
		return 'Deze week';
	}

	// Last week (previous ISO week)
	const lastWeekStart = addDays(getStartOfISOWeek(now), -7);
	const lastWeekEnd = addDays(lastWeekStart, 6);
	if (chatDate >= lastWeekStart && chatDate <= lastWeekEnd) {
		return 'Vorige week';
	}

	// Normalize both dates to start of day for consistent day-based comparison
	const nowStartOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
	const chatStartOfDay = new Date(chatDate.getFullYear(), chatDate.getMonth(), chatDate.getDate());

	const diffMs = nowStartOfDay.getTime() - chatStartOfDay.getTime();
	const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

	// Weeks (2-3 weeks ago)
	if (diffDays < 28) {
		return `${Math.floor(diffDays / 7)} weken geleden`;
	}

	// Months (1-12 months ago)
	const diffMonths = getMonthsDifference(chatStartOfDay, nowStartOfDay);
	if (diffMonths < 12) {
		return diffMonths === 1 ? '1 maand geleden' : `${diffMonths} maanden geleden`;
	}

	// Years
	const diffYears = getYearsDifference(chatStartOfDay, nowStartOfDay);
	return diffYears === 1 ? '1 jaar geleden' : `${diffYears} jaren geleden`;
}

export function getMonthsDifference(startDate: Date, endDate: Date): number {
	const yearDiff = endDate.getFullYear() - startDate.getFullYear();
	const monthDiff = endDate.getMonth() - startDate.getMonth();
	return yearDiff * 12 + monthDiff;
}

export function getYearsDifference(startDate: Date, endDate: Date): number {
	return endDate.getFullYear() - startDate.getFullYear();
}

/** @return De vorige maand (1-12) */
export function getVorigeMaand(): number {
	return 1 + ((today().getMonth() + 11) % 12);
}

/** @return De huidige maand (1-12) */
export function getHuidigeMaand(): number {
	return 1 + today().getMonth();
}

export type Dagdeel = 'nacht' | 'ochtend' | 'middag' | 'avond';

/**
 * Dagdeel op basis van het uur van de dag (CL-9205 begroeting op de startpagina):
 * 00:00-05:59 nacht, 06:00-11:59 ochtend, 12:00-17:59 middag, 18:00-23:59 avond.
 */
export function getDagdeel(date: Date): Dagdeel {
	const uur = date.getHours();
	if (uur < 6) return 'nacht';
	if (uur < 12) return 'ochtend';
	if (uur < 18) return 'middag';
	return 'avond';
}

/** Vertaalt een dagdeel naar de begroeting die de startpagina toont. */
export function getBegroeting(dagdeel: Dagdeel): string {
	switch (dagdeel) {
		case 'nacht':
			return 'Goedenacht';
		case 'ochtend':
			return 'Goedemorgen';
		case 'middag':
			return 'Goedemiddag';
		case 'avond':
			return 'Goedenavond';
	}
}

/**
 * Tijdsaanduiding voor een bezoekmoment, uitgedrukt in het aantal kalenderdagen geleden (CL-9203
 * Recent bekeken op de startpagina): 'Vandaag', '1 dag geleden', 'N dagen geleden'.
 */
export function getDagenGeledenLabel(datum: Date, vandaag: Date = today()): string {
	const dagenGeleden = dateDiffInDays(datum, vandaag);
	if (dagenGeleden <= 0) return 'Vandaag';
	if (dagenGeleden === 1) return '1 dag geleden';
	return `${dagenGeleden} dagen geleden`;
}
